import { Component, OnDestroy } from '@angular/core';
import { IonicPage, ModalController, ToastController } from 'ionic-angular';
import { GridDataResult, PagerSettings, RowClassArgs } from '@progress/kendo-angular-grid';
import { process, State } from '@progress/kendo-data-query';
import { DateTime } from 'luxon';
import { AppConfig } from '../../app/app.config';
import { CyclesPageData, CycleSummary } from '../../models/cycles/cycle.models';
import { DailyLogPermissions } from '../../models/permissions/permission.models';
import { PtLuxonDate } from '../../models/projects/date.models';
import { SupportContextKey } from '../../models/support/support.models';
import { CyclesService } from '../../services/cycles/cycles.service';
import { DailyLogService } from '../../services/daily-log/daily-log.service';
import { DateService } from '../../services/date/date.service';
import { NavHelperService } from '../../services/nav/nav-helper.service';
import { PermissionsService } from '../../services/permissions/permissions.service';
import { SupportContextService } from '../../services/support/support-context.service';
import { LockedDayService } from '../../services/locked-day/locked-day.service';
import { ImplementerService } from '../../services/implementer/implementer.service';
import { CompleteDayService } from '../../services/complete-day/complete-day.service';
import { LockDayFilters } from '../../models/lock-day.models';
import { CompleteDayFilters } from '../../models/complete-day.models';
import { cloneDeep } from 'lodash';
import { CycleEditorModalComponent, CycleEditorResult } from './cycle-editor-modal/cycle-editor-modal';
import { ConfirmationModalComponent } from '../daily-log/confirmation-modal/confirmation-modal.component';

@IonicPage()
@Component({
  selector: 'page-cycles',
  templateUrl: 'cycles.html'
})
export class CyclesPage implements OnDestroy {
  config = AppConfig.settings;
  loading = true;
  dailyLogStale = false;
  dailyLogDate: PtLuxonDate;
  permissions: DailyLogPermissions = new DailyLogPermissions();
  data: CyclesPageData = new CyclesPageData();
  historyGridData: GridDataResult;
  historyState: State = {skip: 0, take: 10};
  historyPageable: PagerSettings = {type: 'input', pageSizes: [10, 25, 50]};
  activeDurationSeconds = 0;
  canEditDay = false;
  private durationTimer: any;

  constructor(
    private cyclesService: CyclesService,
    private dailyLogService: DailyLogService,
    private dateService: DateService,
    private permissionsService: PermissionsService,
    private modalController: ModalController,
    private toastController: ToastController,
    private navHelperService: NavHelperService,
    private supportContextService: SupportContextService
    , private lockedDayService: LockedDayService
    , private implementerService: ImplementerService
    , private completeDayService: CompleteDayService
  ) {
    this.navHelperService.dailyLogStale.subscribe(value => this.dailyLogStale = value);
  }

  async ionViewDidEnter() {
    this.supportContextService.setComponentContext(SupportContextKey.Cycles, 'Cycles', 'CyclesPage');
    await this.load();
  }

  ngOnDestroy() {
    this.stopDurationTimer();
  }

  ionViewWillLeave() {
    this.stopDurationTimer();
  }

  get canManage(): boolean {
    return this.permissions.canManageDailyLog && !this.dailyLogStale && this.canEditDay;
  }

  async activate() {
    const result = await this.openEditor('activate');
    if (!result || !result.confirmed) {
      return;
    }

    this.applyPageData(result.data);
    this.showToast(`Cycle ${result.ordinal} activated.`);
  }

  async end() {
    const result = await this.openEditor('end');
    if (!result || !result.confirmed) {
      return;
    }

    this.applyPageData(result.data);
    this.showToast(result.startNextCycle ? 'Cycle ended and the next cycle started.' : 'Cycles deactivated.');
  }

  async edit(cycle: CycleSummary) {
    const result = await this.openEditor('edit', cycle);
    if (result && result.confirmed) {
      this.applyPageData(result.data);
      this.showToast(`Cycle ${cycle.ordinal} updated.`);
    }
  }

  delete(cycle: CycleSummary) {
    const confirmationModal = this.modalController.create(ConfirmationModalComponent, {
      title: 'Delete Confirmation',
      message: `Are you sure you want to delete Cycle ${cycle.ordinal}?`,
      optionalMessage: 'This will deactivate cycles. The previous cycle will remain ended.',
      trueText: 'Confirm',
      falseText: 'Cancel',
      showButtons: true
    }, {cssClass: 'daily-log-modal-three-line'});

    confirmationModal.onDidDismiss(data => {
      if (data && data.choice) {
        this.deleteConfirmed(cycle);
      }
    });
    confirmationModal.present();
  }

  async deleteConfirmed(cycle: CycleSummary) {
    try {
      this.loading = true;
      this.applyPageData(await this.cyclesService.deleteCycle(cycle.id, this.dailyLogDate.dateTime));
      this.showToast(`Cycle ${cycle.ordinal} deleted.`);
    } catch (error) {
      this.showError(error, 'The cycle could not be deleted.');
    } finally {
      this.loading = false;
    }
  }

  canChange(cycle: CycleSummary, end: boolean = false): boolean {
    if (!this.canManage || !cycle || (end && !cycle.endDateUtc)) return false;
    const date = end ? cycle.endDateUtc : cycle.startDateUtc;
    return date.year === this.dailyLogDate.dateTime.year && date.month === this.dailyLogDate.dateTime.month &&
      date.day === this.dailyLogDate.dateTime.day;
  }

  canEdit(cycle: CycleSummary): boolean {
    return this.canChange(cycle) || this.canChange(cycle, true);
  }

  isLatest(cycle: CycleSummary): boolean {
    return !!cycle && !!this.data.cycles.length && this.data.cycles[0].id === cycle.id;
  }

  formatDate(date: DateTime): string {
    return date ? this.dateService.setFullTimeString(date) : 'Open';
  }

  formatDuration(cycle: CycleSummary): string {
    if (cycle.durationSeconds == null) {
      return 'In progress';
    }

    return this.formatDurationSeconds(cycle.durationSeconds);
  }

  formatActiveDuration(): string {
    return this.formatDurationSeconds(this.activeDurationSeconds);
  }

  historyStateChange(state: State) {
    this.historyState = state;
    this.updateHistoryGrid();
  }

  cycleRowClass = (context: RowClassArgs) => ({'active-row': context.dataItem.isActive});

  private formatDurationSeconds(durationSeconds: number): string {
    const totalMinutes = Math.max(0, Math.floor(durationSeconds / 60));
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    return `${days ? days + 'd ' : ''}${hours ? hours + 'h ' : ''}${minutes}m`;
  }

  private async load() {
    this.loading = true;
    try {
      this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();
      const lockFilters = new LockDayFilters();
      lockFilters.date = cloneDeep(this.dailyLogDate.dateTime);
      const completeFilters: CompleteDayFilters = {date: this.dailyLogDate.dateTime};
      const access = await Promise.all([
        this.permissionsService.dailyLogPermissions(),
        this.lockedDayService.checkDay(lockFilters),
        this.completeDayService.checkDay(completeFilters),
        this.implementerService.implementerIsActive(this.config.isVesselEnvironment, cloneDeep(this.dailyLogDate))
      ]);
      this.permissions = access[0] as DailyLogPermissions;
      const dayCompleted = access[2] as boolean;
      const dayLockedByDate = (access[1] as any).lockedByDate;
      this.canEditDay = (!dayCompleted || dayLockedByDate) &&
        (!this.config.isImplementerEnvironment || access[3] as boolean);
      this.applyPageData(await this.cyclesService.getPageData(this.dailyLogDate.dateTime));
    } catch (error) {
      this.showError(error, 'Cycles could not be loaded.');
    } finally {
      this.loading = false;
    }
  }

  private applyPageData(data: CyclesPageData) {
    this.data = data || new CyclesPageData();
    this.historyState = {...this.historyState, skip: 0};
    this.updateHistoryGrid();
    this.startDurationTimer();
  }

  private updateHistoryGrid() {
    this.historyGridData = process(this.data.cycles || [], this.historyState);
  }

  private startDurationTimer() {
    this.stopDurationTimer();
    this.updateActiveDuration();

    if (this.data.activeCycle) {
      this.durationTimer = setInterval(() => this.updateActiveDuration(), 30000);
    }
  }

  private stopDurationTimer() {
    if (this.durationTimer) {
      clearInterval(this.durationTimer);
      this.durationTimer = null;
    }
  }

  private updateActiveDuration() {
    this.activeDurationSeconds = this.data.activeCycle
      ? Math.max(0, Math.floor(DateTime.utc().diff(this.data.activeCycle.startDateUtc.toUTC(), 'seconds').seconds))
      : 0;
  }

  private openEditor(mode: 'activate' | 'end' | 'edit', selectedCycle?: CycleSummary): Promise<CycleEditorResult> {
    const editedCycle = selectedCycle || this.data.activeCycle;
    const modal = this.modalController.create(CycleEditorModalComponent, {
      mode: mode,
      cycles: this.data.cycles,
      dailyLogDate: this.dailyLogDate,
      suggestedOrdinal: this.data.suggestedOrdinal,
      canChooseOrdinal: !this.data.cycles.some(cycle => cycle.projectId === this.data.projectId),
      previousCycle: this.data.cycles.find(cycle =>
        cycle.projectId === (editedCycle ? editedCycle.projectId : this.data.projectId) &&
        cycle.ordinal === (editedCycle ? editedCycle.ordinal - 1 : this.data.suggestedOrdinal - 1)),
      nextCycle: editedCycle ? this.data.cycles.find(cycle =>
        cycle.projectId === editedCycle.projectId && cycle.ordinal === editedCycle.ordinal + 1) : null,
      activeCycle: editedCycle,
      canEditStart: mode === 'edit' && this.canChange(editedCycle),
      canEditEnd: mode === 'edit' && this.canChange(editedCycle, true)
    }, {
      cssClass: 'cycle-editor-dialog',
      enableBackdropDismiss: false
    });

    return new Promise(resolve => {
      modal.onDidDismiss(result => resolve(result));
      modal.present();
    });
  }

  private showError(error: any, fallback: string) {
    const apiError = error && error.error;
    const message = typeof apiError === 'string'
      ? apiError
      : apiError && apiError.message ? apiError.message : fallback;
    this.showToast(message);
  }

  private showToast(message: string) {
    this.toastController.create({message: message, duration: 4000, position: 'bottom'}).present();
  }
}



// WEBPACK FOOTER //
// ./src/pages/cycles/cycles.ts